import { TR4E, TR4ERead } from '@ncss/models/dist/v3';
import { shallowRef, ShallowRef } from 'vue';
import { DirectConnectEndDevice } from './base-end-device';
import { EndDeviceMessage } from '../messages/endDeviceMessage';
import { OverallStatus } from '../../base/utils';
import { ConversionUtils, TR4EInfo, TR4EInfoMessageType, TR4ERfMode } from '@ncss/models';
import { EndDeviceInfoMessage } from '../messages/endDeviceInfoMessage';

export class DirectConnectTR4E extends DirectConnectEndDevice {
  get firmwareVersionStr() {
    if (this._tr4e.value) {
      return this._tr4e.value?.firmwareVersionMajor + '.' + this._tr4e.value?.firmwareVersionMinor;
    }
    return '';
  }
  get hardwareVersionStr() {
    if (this._tr4e.value) {
      return this._tr4e.value.hardwareVersionMajor + '.' + this._tr4e.value.hardwareVersionMinor;
    }
    return '';
  }
  /**
   * The read provided in the most recent message from the TR4-E.
   */
  get meterRead() {
    return this._meterRead.value;
  }
  /**
   * The TR4-E device object provided in the most recent message.
   */
  get tr4e() {
    return this._tr4e.value;
  }
  /**
   * A computed status object for the TR4-E.
   * The status is computed based on the signal strength and whether the TR4-E has received a response from the gateway.
   *
   * The status object contains the following properties:
   * - isOk: A boolean indicating whether the TR4-E is in a good state.
   * - label: A string indicating the status of the TR4-E.
   * - description: A string describing the status of the TR4-E.
   */
  get status() {
    return this._status.value;
  }
  get rapidCheckInEnabled() {
    return !!this._tr4e.value?.rapidCheckInEnabled;
  }
  get temperatureStr() {
    if (!this._tr4e.value) {
      return undefined;
    }
    if (this._useMetric) {
      return this._tr4e.value.temperature + ' C';
    }
    return (ConversionUtils.ConvertCelsiusToFahrenheit(this._tr4e.value.temperature)?.toFixed(1) || 0) + ' F';
  }

  private _tr4e: ShallowRef<TR4E | undefined> = shallowRef();
  private _meterRead: ShallowRef<TR4ERead | undefined> = shallowRef();
  private _status: ShallowRef<OverallStatus | undefined> = shallowRef();

  constructor(msg: EndDeviceMessage, useMetric: boolean) {
    super(msg, useMetric);
    this.updateFromEndDeviceMessage(msg);
  }

  updateFromEndDeviceMessage(msg: EndDeviceMessage): void {
    if (msg.device instanceof TR4E) {
      this._tr4e.value = msg.device;
      this._lastMsgAt.value = new Date();
      this._firstHopRssi.value = msg.firstHopRssi;
      const reads = TR4ERead.Create(msg.srcAddr, msg.device, {
        firstHopId: 0,
        firstHopRssi: 0,
        lastHopRssi: 0,
        isProgrammed: false,
      });
      this._meterRead.value = reads[0];
      this._status.value = this.computeOverallStatus(msg.device);
    }
  }

  getRapidCheckInMsg(enabled: boolean): EndDeviceInfoMessage {
    const msg = new EndDeviceInfoMessage(this.deviceId, TR4EInfoMessageType.DC301);
    if (msg.info instanceof TR4EInfo) {
      msg.info.userMsgResponse = true;
      msg.info.serialNumber = this.deviceId;
      if (enabled) {
        msg.info.startRapidCheckIn = true;
      } else {
        msg.info.stopRapidCheckIn = true;
      }
    }
    return msg;
  }

  getLegacyModeMsg(enabled: boolean): EndDeviceInfoMessage {
    const msg = new EndDeviceInfoMessage(this.deviceId, TR4EInfoMessageType.DC301);
    if (msg.info instanceof TR4EInfo) {
      msg.info.userMsgResponse = true;
      msg.info.serialNumber = this.deviceId;
      if (enabled) {
        msg.info.setRFMode(TR4ERfMode.LEGACY);
      } else {
        msg.info.setRFMode(TR4ERfMode.HIGH_POWER);
      }
    }
    return msg;
  }

  private computeOverallStatus(tr4: TR4E): OverallStatus {
    const status: OverallStatus = {
      isOk: true,
      label: '',
      description: '',
    };

    status.isOk = tr4.gatewayReplied && this.signalStrengthStr !== 'Poor';
    status.label =
      this.signalStrengthStr === 'Poor'
        ? 'Poor Transceiver Signal'
        : tr4.gatewayReplied
        ? 'All Good'
        : 'Not Communicating With Gateway';
    status.description = tr4.gatewayReplied
      ? `This device has a${this.signalStrengthStr === 'Excellent' ? 'n' : ''} ${
          this.signalStrengthStr
        } connection to the Gateway.`
      : 'The Transceiver has not received a response from any nearby Gateways. This could mean that the device is either not programmed to a Unit yet or it is not within range of a Gateway.';

    return status;
  }
}
